import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import { IdFieldSelect } from '../../components/formFields/IdFieldSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  createPoliceStation,
  getPoliceStationById,
  listPoliceDivisions,
  updatePoliceStation,
  type PoliceDivisionOption,
} from '../../services/policeStations';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../utils/requiredValidation';
import { useAuth } from '../../contexts/AuthContext';

export default function PoliceStationFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { policeStationId } = useParams();
  const isEdit = Boolean(policeStationId);
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [policeDivisions, setPoliceDivisions] = useState<PoliceDivisionOption[]>([]);
  const [divisionsLoading, setDivisionsLoading] = useState(true);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [policeDivisionId, setPoliceDivisionId] = useState('');

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setDivisionsLoading(true);
    void listPoliceDivisions().then((res) => {
      if (!alive) return;
      setPoliceDivisions(
        res.success ? [...res.data].sort((a, b) => a.name.localeCompare(b.name)) : [],
      );
      setDivisionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!isEdit || !policeStationId) return;
    let alive = true;
    setLoading(true);
    void getPoliceStationById(policeStationId).then((res) => {
      if (!alive) return;
      if (res.success) {
        setName(res.data.name);
        setDescription(res.data.description);
        setActive(res.data.active);
        setPoliceDivisionId(res.data.policeDivisionId);
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, policeStationId, t]);

  const divisionSelectOptions = useMemo(
    () =>
      policeDivisions.map((division) => ({
        id: division.id,
        label: `${division.name} (${division.districtName})`,
      })),
    [policeDivisions],
  );

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(
    () => [
      {
        key: 'name',
        label: t('metadata.policeStationForm.name'),
        value: name,
        elementId: 'policeStation-name',
      },
      {
        key: 'policeDivisionId',
        label: t('metadata.policeStationForm.policeDivision'),
        value: policeDivisionId,
        elementId: 'policeStation-division',
      },
    ],
    [name, policeDivisionId, t],
  );

  async function handleSave() {
    if (savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (name.trim().length > 100) {
      errs.name = t('metadata.policeStationForm.nameLengthError');
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(requiredSpecs, errs);
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      active,
      policeDivisionId,
    };

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      if (isEdit && policeStationId) {
        const res = await updatePoliceStation(policeStationId, payload);
        if (!res.success) {
          setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
          return;
        }
        setFlashToast({ message: t('metadata.policeStationForm.updateSuccess'), variant: 'success' });
      } else {
        const res = await createPoliceStation(payload);
        if (!res.success) {
          setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
          return;
        }
        setFlashToast({ message: t('metadata.policeStationForm.createSuccess'), variant: 'success' });
      }
      nav('/metadata/police-stations');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/police-stations">{t('metadata.policeStations.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('metadata.policeStationForm.updateTitle') : t('metadata.policeStationForm.addTitle')}
      </div>

      <div className="pg-title">
        {isEdit ? t('metadata.policeStationForm.updateTitle') : t('metadata.policeStationForm.addTitle')}
      </div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f">
              <div className="al-l">
                {t('metadata.policeStationForm.name')} <span className="req">*</span>
              </div>
              <input
                id="policeStation-name"
                className="al-inp"
                value={name}
                placeholder={t('metadata.policeStationForm.namePlaceholder')}
                maxLength={100}
                disabled={loading}
                onChange={(e) => {
                  setName(e.target.value);
                  clearReqError('name');
                }}
                style={reqErrors.name ? { borderColor: 'var(--red)' } : {}}
                aria-invalid={reqErrors.name ? true : undefined}
              />
              {reqErrors.name ? (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>{reqErrors.name}</div>
              ) : null}
            </div>

            <IdFieldSelect
              id="policeStation-division"
              label={t('metadata.policeStationForm.policeDivision')}
              required
              value={policeDivisionId}
              placeholder={
                divisionsLoading ? t('common.loading') : t('metadata.policeStationForm.selectPoliceDivision')
              }
              options={divisionSelectOptions}
              error={reqErrors.policeDivisionId}
              disabled={divisionsLoading || loading}
              onChange={(v) => {
                setPoliceDivisionId(v);
                clearReqError('policeDivisionId');
              }}
            />

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <div className="al-l">{t('metadata.policeStationForm.description')}</div>
              <input
                id="policeStation-description"
                className="al-inp"
                value={description}
                placeholder={t('metadata.policeStationForm.descriptionPlaceholder')}
                disabled={loading}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={active}
                  disabled={loading}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('metadata.policeStationForm.active')}</span>
              </label>
            </div>
          </div>
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('admin.shared.cancelChangesMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/metadata/police-stations');
        }}
      />
    </>
  );
}
